/**
 * loadPaystackSDK.ts
 *
 * Utility for dynamically loading the Paystack Inline.js SDK
 * Implements promise-based loading with caching and error handling
 */

// Paystack SDK URL
const PAYSTACK_SDK_URL = 'https://js.paystack.co/v2/inline.js';

// SDK loading state
type SDKLoadState = 'idle' | 'loading' | 'loaded' | 'error';

let loadState: SDKLoadState = 'idle';
let loadPromise: Promise<void> | null = null;

/**
 * Check if Paystack SDK is already loaded
 */
export function isPaystackSDKLoaded(): boolean {
  return typeof window !== 'undefined' && !!(window as any).PaystackPop;
}

/**
 * Load Paystack SDK dynamically
 * Returns a promise that resolves when SDK is ready
 * Uses caching to avoid duplicate loads
 *
 * @returns Promise that resolves when SDK is loaded
 * @throws Error if SDK fails to load
 */
export function loadPaystackSDK(): Promise<void> {
  // Server-side rendering check
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Paystack SDK can only be loaded in browser environment'));
  }

  // Already loaded
  if (isPaystackSDKLoaded()) {
    loadState = 'loaded';
    return Promise.resolve();
  }

  // Currently loading - return existing promise
  if (loadState === 'loading' && loadPromise) {
    return loadPromise;
  }

  // Start loading
  loadState = 'loading';

  loadPromise = new Promise<void>((resolve, reject) => {
    // Check if script tag already exists
    const existingScript = document.querySelector(`script[src="${PAYSTACK_SDK_URL}"]`);

    if (existingScript) {
      // Script tag exists, wait for load
      if (isPaystackSDKLoaded()) {
        loadState = 'loaded';
        resolve();
      } else {
        existingScript.addEventListener('load', () => {
          loadState = 'loaded';
          resolve();
        });
        existingScript.addEventListener('error', () => {
          loadState = 'error';
          reject(new Error('Failed to load Paystack SDK'));
        });
      }
      return;
    }

    // Create new script tag
    const script = document.createElement('script');
    script.src = PAYSTACK_SDK_URL;
    script.async = true;

    script.onload = () => {
      // Verify SDK loaded correctly
      if (isPaystackSDKLoaded()) {
        loadState = 'loaded';
        resolve();
      } else {
        loadState = 'error';
        reject(new Error('Paystack SDK loaded but PaystackPop not available'));
      }
    };

    script.onerror = () => {
      loadState = 'error';
      // Remove failed script tag
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      reject(new Error('Failed to load Paystack SDK script'));
    };

    // Append to document head
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Load Paystack SDK with retry logic
 * Attempts to load SDK with exponential backoff
 *
 * @param maxRetries Maximum number of retry attempts (default: 3)
 * @param initialDelay Initial delay in ms (default: 1000)
 * @returns Promise that resolves when SDK is loaded
 */
export async function loadPaystackSDKWithRetry(
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<void> {
  let lastError: Error | null = null;
  let delay = initialDelay;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      await loadPaystackSDK();
      return; // Success
    } catch (error) {
      lastError = error as Error;
      console.warn(`Paystack SDK load attempt ${attempt}/${maxRetries} failed:`, error);

      if (attempt < maxRetries) {
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // Double delay for next attempt

        // Reset state to retry
        loadState = 'idle';
        loadPromise = null;

        // Remove failed script tags
        const failedScripts = document.querySelectorAll(`script[src="${PAYSTACK_SDK_URL}"]`);
        failedScripts.forEach(script => script.remove());
      }
    }
  }

  // All retries failed
  throw new Error(
    `Failed to load Paystack SDK after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`
  );
}

/**
 * Get current SDK load state
 */
export function getPaystackSDKLoadState(): SDKLoadState {
  if (isPaystackSDKLoaded()) {
    return 'loaded';
  }
  return loadState;
}

/**
 * Reset SDK load state (useful for testing)
 */
export function resetPaystackSDKState(): void {
  loadState = 'idle';
  loadPromise = null;
}
